import React, { useEffect, useState } from 'react'
import Select, { StylesConfig } from 'react-select'
import moment, { Moment } from 'moment'
import clientApi from 'services/clientApi'
import projectApi from 'services/projectApi'
import userApi from 'services/userApi'
import { setLabel } from 'components/utils/SelectFunctions'
import { Filter } from 'Types/Filter'
import { ILabel } from 'Types/ILabel'
import styles from './styles.module.scss'

export type PeriodUnit = 'month' | 'year'

interface IProps {
  filters: Filter[]
  setFilters: (update: (filters: Filter[]) => Filter[]) => void
}

const DATE_FORMAT = 'YYYY-MM-DD'

const ENTITY_FILTERS = [
  { key: 'clientId', label: 'Client', placeholder: 'All clients' },
  { key: 'projectId', label: 'Project', placeholder: 'All projects' },
  { key: 'userId', label: 'Employee', placeholder: 'All employees' },
]

/** Start/end date filters for the given period */
export function periodFilters(start: Moment, unit: PeriodUnit): Filter[] {
  return [
    new Filter('startDate', start.clone().startOf(unit).format(DATE_FORMAT)),
    new Filter('endDate', start.clone().endOf(unit).format(DATE_FORMAT)),
  ]
}

// react-select restyled to match the app's inputs; an active filter gets a tinted background
const selectStyles: StylesConfig<ILabel, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderRadius: 7,
    backgroundColor: state.hasValue ? '#f1f7f9' : '#ffffff',
    borderColor: state.isFocused || state.hasValue ? '#3e88a3' : '#e3e3e3',
    boxShadow: state.isFocused ? '0 0 0 0.15rem rgba(62, 136, 163, 0.2)' : 'none',
    '&:hover': { borderColor: '#3e88a3' },
  }),
  placeholder: (base) => ({ ...base, color: '#7a8a91' }),
  singleValue: (base) => ({ ...base, color: '#3a3a3a', fontWeight: 500 }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({ ...base, color: '#7a8a91', padding: '0 8px' }),
  clearIndicator: (base) => ({ ...base, padding: '0 4px' }),
  menu: (base) => ({ ...base, zIndex: 10, borderRadius: 7, overflow: 'hidden' }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#3e88a3' : state.isFocused ? '#e8f1f5' : undefined,
    color: state.isSelected ? '#ffffff' : '#3a3a3a',
  }),
}

const ReportsFilters: React.FC<IProps> = (props: IProps) => {
  const [options, setOptions] = useState<Record<string, ILabel[]>>({})
  const [unit, setUnit] = useState<PeriodUnit>('month')

  const startFilter = props.filters.find((filter) => filter.key === 'startDate')
  const periodStart = moment(startFilter?.value)
  const periodLabel =
    unit === 'month' ? periodStart.format('MMMM YYYY') : periodStart.format('YYYY')
  const hasEntityFilter = props.filters.some((filter) =>
    ENTITY_FILTERS.some((entity) => entity.key === filter.key)
  )

  useEffect(() => {
    clientApi
      .getClients()
      .then((response) =>
        setOptions((old) => ({ ...old, clientId: setLabel(response, 'clientName') }))
      )
    projectApi
      .getProjects()
      .then((response) =>
        setOptions((old) => ({ ...old, projectId: setLabel(response.content, 'projectName') }))
      )
    userApi
      .getUsers()
      .then((response) => setOptions((old) => ({ ...old, userId: setLabel(response, 'fullName') })))
  }, [])

  function setPeriod(start: Moment, newUnit: PeriodUnit) {
    setUnit(newUnit)
    props.setFilters((old) => [
      ...old.filter((filter) => filter.key !== 'startDate' && filter.key !== 'endDate'),
      ...periodFilters(start, newUnit),
    ])
  }

  function changeEntity(key: string, selected: ILabel | null) {
    props.setFilters((old) => {
      const others = old.filter((filter) => filter.key !== key)
      return selected ? [...others, new Filter(key, selected.value, selected.label)] : others
    })
  }

  function clearEntities() {
    props.setFilters((old) =>
      old.filter((filter) => !ENTITY_FILTERS.some((entity) => entity.key === filter.key))
    )
  }

  return (
    <div className={styles.filterBar}>
      <div className={styles.filterField}>
        <span className={styles.filterLabel}>Period</span>
        <div className={styles.period}>
          <button
            type="button"
            className={styles.periodNav}
            aria-label={`Previous ${unit}`}
            onClick={() => setPeriod(periodStart.clone().subtract(1, unit), unit)}
          >
            ‹
          </button>
          <span className={styles.periodLabel}>{periodLabel}</span>
          <button
            type="button"
            className={styles.periodNav}
            aria-label={`Next ${unit}`}
            onClick={() => setPeriod(periodStart.clone().add(1, unit), unit)}
          >
            ›
          </button>
          <div className={styles.periodUnits} role="group" aria-label="Period length">
            {(['month', 'year'] as PeriodUnit[]).map((option) => (
              <button
                type="button"
                key={option}
                aria-pressed={unit === option}
                className={`${styles.periodUnit} ${unit === option ? styles.periodUnitActive : ''}`}
                onClick={() =>
                  // Year -> Month lands on the current month when viewing the current year
                  setPeriod(
                    option === 'month' && periodStart.isSame(moment(), 'year')
                      ? moment()
                      : periodStart,
                    option
                  )
                }
              >
                {option === 'month' ? 'Month' : 'Year'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {ENTITY_FILTERS.map((entity) => {
        const active = props.filters.find((filter) => filter.key === entity.key)
        return (
          <div className={styles.filterField} key={entity.key}>
            <label className={styles.filterLabel} htmlFor={entity.key}>
              {entity.label}
            </label>
            <Select<ILabel, false>
              inputId={entity.key}
              isClearable
              options={options[entity.key]}
              value={active ? { value: active.value, label: active.label } : null}
              onChange={(selected) => changeEntity(entity.key, selected)}
              placeholder={entity.placeholder}
              styles={selectStyles}
            />
          </div>
        )
      })}

      {hasEntityFilter && (
        <button type="button" className={styles.clearFilters} onClick={clearEntities}>
          Clear filters
        </button>
      )}
    </div>
  )
}

export default ReportsFilters
